/**
 * The rider's progress through one morning, as a single ordered track.
 *
 * A sketch: nothing reads this yet. It lives here so the shape can be argued about in tests
 * rather than discovered on a platform, and so the transitions no phone can see show up as
 * holes in the model rather than as bugs.
 *
 * The journey only ever runs one way. There is no branching — every phase has one predecessor
 * and one successor — so "further along" is just a position on this list. The rider can step it
 * on when the phone has not noticed, or step it back when a press was wrong.
 */

import type { RiderPhase } from "../shared/types";

/** In order. Index is the whole ordering model. */
export const RIDER_PHASES: RiderPhase[] = [
	"planning",
	"at_home",
	"driving",
	"at_station",
	"on_platform",
	"on_train",
	"at_destination",
	"arrived",
];

/** The rider's words for where they are, not the app's. */
export const RIDER_PHASE_LABELS: Record<RiderPhase, string> = {
	planning: "Planning",
	at_home: "At home",
	driving: "Driving",
	at_station: "At station",
	on_platform: "On platform",
	on_train: "On train",
	at_destination: "Off the train",
	arrived: "Arrived",
};

/**
 * Phases no location signal can detect on its own.
 *
 * Passing the faregates leaves no trace, and the train pulls out of an underground platform in
 * the city, where there is no fix to reason from. These two are why the nudge buttons exist.
 * The timetable can still speak for one of them — see `boardDepartedTrain` — but the phone
 * cannot.
 */
export const UNOBSERVABLE_PHASES: RiderPhase[] = ["on_platform", "on_train"];

/** What the rider would say, offered as a button rather than as a step to be found and held. */
export const DECLARE_LABELS: Partial<Record<RiderPhase, string>> = {
	driving: "I'm in the car",
	on_platform: "I'm on the platform",
	on_train: "I'm on the train",
};

/**
 * The steps worth a button of their own, and the two different reasons they are.
 *
 * `on_platform` and `on_train` are in `UNOBSERVABLE_PHASES`: the faregates and an underground
 * departure leave no trace, so without a button the journey stops there.
 *
 * `driving` is nothing like that — it is observed, and observed well. On 2026-08-31 two phones rode
 * in the same car and the one with the app in front of it called the departure 55 seconds after the
 * wheels turned. The other was behind Waze, its page suspended, and took seven and a half minutes;
 * by then the cron had already told its owner it was time to leave. So the button is not here
 * because the phone cannot see the drive start. It is here because the phone may be asleep when it
 * does, and saying so takes one tap that the driver is reaching for the phone to make anyway.
 */
export const DECLARABLE_PHASES: RiderPhase[] = ["driving", ...UNOBSERVABLE_PHASES];

/**
 * The step waiting on the rider, if the next one is a step the phone can never take itself.
 *
 * These are the only two moments the journey cannot move itself past, so they are the only two
 * worth a button of their own — the rest of the track stays on the progress line, where nothing
 * is asking to be pressed.
 */
export function nextDeclarablePhase(phase: RiderPhase, phases: RiderPhase[] = RIDER_PHASES): RiderPhase | null {
	const next = phases[phases.indexOf(phase) + 1];
	return next && DECLARABLE_PHASES.includes(next) ? next : null;
}

/** Once the morning is this far behind him, the next one is the one worth planning. */
export const RESET_AFTER_ARRIVAL_MS = 30 * 60_000;

/** What moved the journey into its current phase. */
export type PhaseSource = "default" | "observed" | "declared";

export interface JourneyProgress {
	phase: RiderPhase;
	source: PhaseSource;
	/** When the journey entered this phase, so a stalled one can be noticed. */
	sinceMs: number;
	/**
	 * The last thing the evidence said.
	 *
	 * Held because almost nothing the phone knows is an event. "Away from home" stays true for
	 * the whole drive, so a condition is only allowed to speak when it *changes*; left to repeat
	 * itself it would undo a correction on the very next fix.
	 */
	observed: RiderPhase | null;
}

export const INITIAL_PROGRESS: JourneyProgress = { phase: "planning", source: "default", sinceMs: 0, observed: null };

/**
 * Where a journey begins.
 *
 * The ride home has no house to leave and no drive to plan, so it starts where the rider
 * actually is: at school, with the walk to Rockridge still ahead of him.
 */
export function startPhaseFor(hasAccessLeg: boolean): RiderPhase {
	return hasAccessLeg ? "planning" : "at_home";
}

export function initialProgressFor(hasAccessLeg: boolean): JourneyProgress {
	return { phase: startPhaseFor(hasAccessLeg), source: "default", sinceMs: 0, observed: null };
}

export function phaseIndex(phase: RiderPhase): number {
	return RIDER_PHASES.indexOf(phase);
}

/** True when `phase` comes before `other` on the track. */
export function isBefore(phase: RiderPhase, other: RiderPhase): boolean {
	return phaseIndex(phase) < phaseIndex(other);
}

export function nextPhase(phase: RiderPhase): RiderPhase | null {
	return RIDER_PHASES[phaseIndex(phase) + 1] ?? null;
}

export function previousPhase(phase: RiderPhase): RiderPhase | null {
	return phaseIndex(phase) <= 0 ? null : RIDER_PHASES[phaseIndex(phase) - 1];
}

/**
 * The phases a journey actually passes through.
 *
 * The ride home has no morning to plan and no drive to make: he walks out of school and the
 * next thing that happens is Rockridge. Steps that can never be taken are worse than absent —
 * they are two more things to swipe past.
 */
export function phasesForJourney(hasAccessLeg: boolean): RiderPhase[] {
	return hasAccessLeg ? RIDER_PHASES : RIDER_PHASES.filter((phase) => phase !== "planning" && phase !== "driving");
}

/**
 * The rider's word for a phase, which is not the same in both directions.
 *
 * "Arrived" says where, because the journey has two endings that are nothing alike and the app
 * already has a separate word for reaching a platform. Off the train at Rockridge is not home.
 */
export function phaseLabel(phase: RiderPhase, hasAccessLeg: boolean): string {
	if (phase === "arrived") {
		return hasAccessLeg ? "Arrived school" : "Arrived home";
	}
	if (phase === "at_home" && !hasAccessLeg) {
		return "At school";
	}
	return RIDER_PHASE_LABELS[phase];
}

/**
 * How long a fresh declaration is protected from being walked back.
 *
 * The next-step button is laid *over* the progress line and vanishes the moment it is pressed,
 * because there is no declarable step after `on_train`. On 2026-09-01 that left the line exactly
 * where the thumb already was: a second press two seconds later put Eben back on the platform he
 * had just left, and nothing could contradict it for nearly four minutes. Two seconds is long
 * enough to cover the stray press and far too short to matter to anyone correcting a real mistake.
 */
export const DECLARE_SETTLE_MS = 2_000;

/**
 * A step the rider took by hand, to correct the phase or to trigger one early.
 *
 * Goes either way and is otherwise always obeyed: the rider is standing there and the app is not.
 * The one exception is going *backwards* within `DECLARE_SETTLE_MS` of declaring forwards, which
 * is the shape of a mis-tap rather than a correction.
 */
export function declarePhase(progress: JourneyProgress, phase: RiderPhase, nowMs: number): JourneyProgress {
	if (phase === progress.phase) {
		return progress;
	}
	const undoingAFreshStep =
		progress.source === "declared" && phaseIndex(phase) < phaseIndex(progress.phase) && nowMs - progress.sinceMs < DECLARE_SETTLE_MS;
	if (undoingAFreshStep) {
		return progress;
	}
	return { ...progress, phase, source: "declared", sinceMs: nowMs };
}

export interface PhaseSignals {
	/** The morning this deadline belongs to is live, i.e. inside the alert window. */
	withinWindow: boolean;
	/** Away from the saved starting point and away from every known house. */
	awayFromHome: boolean;
	/** Latched on arrival at the boarding station. */
	atStation: boolean;
	/** Latched at the far-end station, once his train is in and the fix has held. */
	atDestination: boolean;
	/** Latched at the place past the platform he was actually going to. */
	arrived: boolean;
	/** Under the bay, which nothing but a train explains. */
	inTube?: boolean;
}

/**
 * The furthest phase the evidence can vouch for.
 *
 * The faregates leave no trace, which is why on_platform is not here. Neither is on_train, with
 * the one exception of the tube: there is no other way to be under the bay.
 */
export function observablePhase(signals: PhaseSignals, phases: RiderPhase[] = RIDER_PHASES, automatic = true): RiderPhase | null {
	if (!automatic) return null;
	const seen = signals.arrived
		? "arrived"
		: signals.atDestination
			? "at_destination"
			: signals.inTube
				? "on_train"
				: signals.atStation
					? "at_station"
					: signals.awayFromHome
						? "driving"
						: signals.withinWindow
							? "at_home"
							: null;
	return seen != null && phases.includes(seen) ? seen : null;
}

/**
 * What the evidence implies, which may only ever move the journey on.
 *
 * Two rules, and both exist because the rider has to be able to disagree. Evidence never
 * rewinds, so a bad fix at the station cannot put him back in the car. And it only speaks on a
 * change, so stepping back out of a phase the phone still believes in actually sticks — until
 * the evidence says something new, which is how it recovers on its own.
 */
export function observePhase(progress: JourneyProgress, observed: RiderPhase | null, nowMs: number): JourneyProgress {
	if (observed === progress.observed) {
		return progress;
	}
	const heard = { ...progress, observed };
	if (observed == null || !isBefore(progress.phase, observed)) {
		return heard;
	}
	return { phase: observed, source: "observed", sinceMs: nowMs, observed };
}

export function scheduledProgress(progress: JourneyProgress, hasAccessLeg: boolean, automatic: boolean): JourneyProgress {
	return !automatic && hasAccessLeg && progress.phase === "at_home" && progress.source !== "declared"
		? initialProgressFor(hasAccessLeg) : progress;
}

/** True once the morning is far enough behind that the journey should start again. */
export function shouldReset(progress: JourneyProgress, nowMs: number): boolean {
	return progress.phase === "arrived" && nowMs - progress.sinceMs >= RESET_AFTER_ARRIVAL_MS;
}

/**
 * A journey belongs to the day it started, and midnight ends it however it was going.
 *
 * `shouldReset` only ever fires for one that reached `arrived`, so a journey abandoned part-way —
 * a holiday, a lift home, a phone that went quiet underground — had no way to end at all. On
 * 2026-09-07 a `driving` declared at 07:00 was still on screen at 05:53 the next morning, and on
 * 2026-09-08 an `on_train` outlived the ride by an hour. Reopening the app closes the old one,
 * because the stored copy is dated; a page the shell keeps alive never gets that chance.
 */
export function startedOnAnotherDay(startedDayKey: string | null, todayKey: string): boolean {
	return startedDayKey != null && startedDayKey !== todayKey;
}

/**
 * A train left the platform he is standing on, so he is on it.
 *
 * Standing there watching one pull in and out, and then being shown the next one nine minutes
 * away, is the app deciding he missed it. Missing a train you are already beside is the rare
 * path, and the step back is there for it.
 *
 * Only departures after he reached the platform count. Without that, stepping back after a
 * genuine miss — or just swiping to look at the train that left — would push him straight
 * forward again.
 */
export function boardDepartedTrain(progress: JourneyProgress, lastDepartureMs: number | null, nowMs: number): JourneyProgress {
	if (progress.phase !== "on_platform" || lastDepartureMs == null || lastDepartureMs < progress.sinceMs) {
		return progress;
	}
	return { ...progress, phase: "on_train", source: "observed", sinceMs: nowMs };
}

/**
 * How many departed trains the deck keeps behind it.
 *
 * Before the station a train that has gone is noise. From the station on it is the one he is
 * asking about, and he has to be able to swipe back to it.
 */
export function departedTrainsToKeep(phase: RiderPhase): number {
	return isBefore(phase, "at_station") ? 0 : 2;
}

/** On board, the only trains he can be on are the ones that have gone or are going now. */
export function isBoarded(phase: RiderPhase): boolean {
	return !isBefore(phase, "on_train");
}

/**
 * Which question the card answers.
 *
 * `leave` counts down to the deadline. `drive` compares drop-off with the train, because the
 * deadline is behind him. `platform` counts the train down. `onboard` stops asking about
 * catching anything at all. `offboard` is the last leg on foot, and `done` is not a question.
 */
export type CardQuestion = "leave" | "drive" | "platform" | "onboard" | "offboard" | "done";

export function cardQuestionFor(phase: RiderPhase): CardQuestion {
	switch (phase) {
		case "planning":
		case "at_home":
			return "leave";
		case "driving":
			return "drive";
		case "at_station":
		case "on_platform":
			return "platform";
		case "on_train":
			return "onboard";
		case "at_destination":
			return "offboard";
		default:
			return "done";
	}
}

/**
 * Which spoken reminders still make sense.
 *
 * Telling someone to leave when they are driving, or that their train is coming when they are
 * sitting on it, is the app talking about a morning the rider has already had. Planning is the
 * other end of the same problem: a morning he is thinking about rather than having, and often
 * not even today's.
 */
export function spokenRemindersFor(phase: RiderPhase): { leave: boolean; train: boolean } {
	if (phase === "planning") {
		return { leave: false, train: false };
	}
	const question = cardQuestionFor(phase);
	return { leave: question === "leave", train: question === "platform" };
}

/**
 * Once he is on his way the target stops moving.
 *
 * Before that the recommendation is free to change as the traffic and the trains do; after it,
 * a train slipping out of reach is worth seeing rather than quietly replaced.
 */
export function isCommitted(phase: RiderPhase): boolean {
	return !isBefore(phase, "driving");
}

/**
 * Whether there is still a drive to route.
 *
 * From the station onwards there is not, and routing one anyway returns the couple of minutes it
 * takes to drive around the block — enough to mark a train he is standing beside unreachable.
 */
export function isDriveOver(phase: RiderPhase): boolean {
	return !isBefore(phase, "at_station");
}

/**
 * Hand the journey back to the phone.
 *
 * A way out of a wrong press without pressing your way back: it drops every declaration and
 * returns to whatever the evidence last vouched for. With nothing observed — outside the
 * morning, or with no location — there is nothing to fall back to but the start.
 */
export function resetToObserved(progress: JourneyProgress, nowMs: number, startPhase: RiderPhase = "planning"): JourneyProgress {
	if (progress.observed == null) {
		return progress.phase === startPhase ? progress : { phase: startPhase, source: "default", sinceMs: 0, observed: null };
	}
	if (progress.observed === progress.phase) {
		return progress;
	}
	return { phase: progress.observed, source: "observed", sinceMs: nowMs, observed: progress.observed };
}
